import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import { FaSpinner, FaTimes, FaCheck, FaSyncAlt } from 'react-icons/fa';
import './RewriteModal.css';
import { rewriteIdea } from '../api/generate';

Modal.setAppElement('#root');

const customStyles = {
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    zIndex: 99999,
  },
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    width: '95%',
    maxWidth: '1200px',
    height: '90vh',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    padding: 0,
    border: 'none',
    borderRadius: '8px',
    background: '#1e1e1e',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
    display: 'flex',
    flexDirection: 'column',
  },
};

interface RewriteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (content: string) => void;
  currentContent: string;
}

const CANNED_PROMPTS = [
  { label: "More Concise", prompt: "Make this idea more concise and focused" },
  { label: "More Detail", prompt: "Expand this idea with more specific details and examples" },
  { label: "Actionable", prompt: "Make this idea more actionable with concrete steps" },
  { label: "Simplify", prompt: "Simplify the language to make it more accessible" },
  { label: "Business View", prompt: "Add business context and market relevance" },
  { label: "Technical View", prompt: "Rewrite from a technical implementation perspective" },
  { label: "User Focus", prompt: "Rewrite focusing on user benefits and experience" },
  { label: "Add Metrics", prompt: "Add specific metrics and success criteria" },
  { label: "Add Risks", prompt: "Add potential risks and mitigation strategies" },
  { label: "Innovate", prompt: "Emphasize innovative and unique aspects" },
  { label: "Cost Focus", prompt: "Add cost considerations and ROI analysis" },
  { label: "Timeline", prompt: "Add implementation timeline and phases" },
];

const RewriteModal: React.FC<RewriteModalProps> = ({
  isOpen,
  onClose,
  onSave,
  currentContent,
}) => {
  const [content, setContent] = useState(currentContent);
  const [prompt, setPrompt] = useState('');
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setContent(currentContent);
  }, [currentContent]);

  const handleRegenerate = async () => {
    if (!prompt.trim() || isRegenerating) return;

    setIsRegenerating(true);
    setError(null);
    try {
      const newContent = await rewriteIdea(content, prompt, 'gpt-4o-mini', true);
      setContent(newContent);
    } catch (error) {
      console.error('Failed to regenerate:', error);
      setError('Failed to rewrite idea. Please try again.');
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleSave = () => {
    if (content.trim()) {
      onSave(content);
      onClose();
    }
  };

  const handlePromptClick = (promptText: string) => {
    setPrompt(promptText);
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      style={customStyles}
      contentLabel="Rewrite Idea"
    >
      <div className="rewrite-modal-header">
        <h2>Rewrite Idea</h2>
        <button className="close-button" onClick={onClose}>
          <FaTimes />
        </button>
      </div>
      
      <div className="rewrite-modal-content">
        <div className="content-layout">
          <div className="edit-area">
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="content-textarea"
              placeholder="Your idea content..."
            />
            {error && <div className="error-message">{error}</div>}
          </div>
          
          <div className="prompt-suggestions">
            <h3>Quick Prompts</h3>
            <div className="prompt-buttons">
              {CANNED_PROMPTS.map((item, index) => (
                <button
                  key={index}
                  className="prompt-button"
                  onClick={() => handlePromptClick(item.prompt)}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="prompt-area">
          <div className="prompt-input-group">
            <input
              type="text"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Enter instructions for AI (e.g., 'Make it more concise', 'Add more detail')"
              className="prompt-input"
            />
            <button
              className="regenerate-button"
              onClick={handleRegenerate}
              disabled={isRegenerating || !prompt.trim()}
            >
              {isRegenerating ? <FaSpinner className="spinner" /> : <FaSyncAlt />}
              Regenerate
            </button>
          </div>
        </div>
      </div>

      <div className="rewrite-modal-footer">
        <button className="cancel-button" onClick={onClose}>
          Cancel
        </button>
        <button
          className="save-button"
          onClick={handleSave}
          disabled={!content.trim()}
        >
          <FaCheck />
          Save Changes
        </button>
      </div>
    </Modal>
  );
};

export default RewriteModal;
